<template>
  <div>
    <SiteHero
      image="/media/samochody-hero.webp"
      eyebrow="Auta dostępne od ręki"
      title="Dostępne pojazdy"
      lead="W naszym salonie znajdziesz auta sprowadzone, gotowe do odbioru lub rejestracji"
      scroll-to="oferta"
    />

    <div class="brands">
      <SiteMarquee :items="brands" :duration="40" />
    </div>

    <section id="oferta" class="site_container offer">
      <div class="offer_intro">
        <div>
          <p class="site_eyebrow"><mark>01</mark> Zakres oferty</p>
          <h2>Szeroki wybór modeli</h2>
          <p class="offer_lead">
            Od codziennych modeli, przez youngtimery, po sportowe i luksusowe egzemplarze
            rzadko spotykane na europejskim rynku.
          </p>

          <div class="offer_filters">
            <input v-model="query" type="search" placeholder="Szukaj modelu" aria-label="Szukaj modelu" />
            <select v-model="brand" aria-label="Marka">
              <option value="">Wybierz model</option>
              <option v-for="name in brandOptions" :key="name" :value="name">{{ name }}</option>
            </select>
          </div>
        </div>

        <div>
          <p class="offer_note">
            Każdy pojazd przeszedł inspekcję, otrzymał komplet dokumentacji i w razie potrzeby
            został przygotowany technicznie w naszym warsztacie. Możemy zorganizować
            ubezpieczenie OC/AC, rejestrację oraz dostawę pod wskazany adres.
          </p>

          <div class="offer_filters">
            <p><strong>Opcje sortowania wyników</strong></p>
            <select v-model="sort" aria-label="Sortowanie">
              <option value="">Według ceny</option>
              <option value="desc">Cena malejąco</option>
              <option value="asc">Cena rosnąco</option>
            </select>
          </div>
        </div>
      </div>

      <ul class="offer_grid">
        <li v-for="car in visibleCars" :key="car.slug" v-reveal>
          <CarCard :car="car" />
        </li>
      </ul>

      <p v-if="!visibleCars.length" class="offer_empty">
        Brak pojazdów spełniających kryteria. Zajrzyj do
        <router-link to="/aukcje/">aukcji</router-link>.
      </p>
    </section>

    <section class="site_container order_wrap">
      <div class="order">
        <p class="site_eyebrow site_eyebrow--accent">Aukcje Japonia Cars</p>
        <h2>
          Jeżeli szukasz konkretnego modelu, którego nie widzisz na liście, zajrzyj do
          zakładki z aukcjami lub zleć nam import na zamówienie!
        </h2>

        <p class="order_actions">
          <router-link to="/aukcje/" class="site_button">Sprawadź aukcje <ArrowIcon /></router-link>
          <router-link to="/samochody/" class="site_button order_button--ghost">Auta od ręki</router-link>
        </p>

        <p class="order_phones">
          <a href="tel:+48509230500"><PhoneIcon /> Tel. 509 230 500</a>
          <a href="tel:+48501069442"><PhoneIcon /> Tel. 501 069 442</a>
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import pageHead from '@/mixins/pageHead'
import { pageSchema } from '@/utils/schema'
import cars from '@/data/cars'
import SiteHero from '@/components/SiteHero.vue'
import SiteMarquee from '@/components/SiteMarquee.vue'
import CarCard from '@/components/CarCard.vue'
import PhoneIcon from '@/components/icons/PhoneIcon.vue'
import ArrowIcon from '@/components/icons/ArrowIcon.vue'

const priceOf = (car) => Number(String(car.price).replace(/[^0-9]/g, '')) || 0

export default {
  name: 'SamochodyPage',
  components: { SiteHero, SiteMarquee, CarCard, PhoneIcon, ArrowIcon },
  mixins: [pageHead],

  pageHead: {
    title: 'Auta dostępne od ręki - Japonia Cars',
    description:
      'Samochody sprowadzone z Japonii, dostępne od ręki — po inspekcji, z pełną dokumentacją.',
    schema: pageSchema({
      path: '/samochody/',
      name: 'Auta dostępne od ręki',
      image: '/media/cars/audi-s5-2018/card.webp',
      breadcrumbs: [{ name: 'Samochody' }],
    }),
  },

  data() {
    return {
      cars,
      query: '',
      brand: '',
      sort: '',
      brands: ['MAZDA', 'SUBARU', 'LEXUS', 'MITSUBISHI', 'SUZUKI', 'NISSAN', 'TOYOTA', 'HONDA'],
    }
  },

  computed: {
    brandOptions() {
      return [...new Set(this.cars.map((car) => car.name.split(' ')[0]))]
    },

    visibleCars() {
      const query = this.query.trim().toLowerCase()

      const found = this.cars.filter(
        (car) =>
          (!query || car.name.toLowerCase().includes(query)) &&
          (!this.brand || car.name.startsWith(this.brand))
      )

      if (!this.sort) return found
      return [...found].sort((a, b) =>
        this.sort === 'asc' ? priceOf(a) - priceOf(b) : priceOf(b) - priceOf(a)
      )
    },
  },
}
</script>

<style scoped>
.brands {
  padding: 36px 0;
  background: var(--paper);
  color: var(--ink-muted);
  font-weight: 700;
}

.brands :deep(.marquee) {
  --marquee-gap: 84px;
}

/* ----------------------------------------------------------- listing --- */

.offer {
  padding-top: 24px;
  padding-bottom: 80px;
}

.offer_intro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: end;
  padding: 64px 0 48px;
}

.offer_intro h2 {
  margin-top: 15px;
}

.offer_lead {
  max-width: 543px;
  margin: 17px 0 30px;
}

.offer_note {
  max-width: 550px;
  margin-bottom: 30px;
}

.offer_intro mark {
  background: none;
  color: var(--accent);
}

.offer_filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.offer_filters input,
.offer_filters select {
  padding: 6.4px 8px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  background: #fff;
  font: inherit;
  color: inherit;
}

.offer_filters input {
  flex: 1;
  min-width: 240px;
}

.offer_grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.offer_empty {
  padding: 48px 0;
  text-align: center;
}

/* ------------------------------------------------------- order a car --- */

.order_wrap {
  padding-bottom: 80px;
}

.order {
  padding: 64px 32px;
  border-radius: var(--radius);
  background: var(--ink);
  color: var(--ink-invert);
  text-align: center;
}

.order .site_eyebrow {
  justify-content: center;
}

.order h2 {
  max-width: 792px;
  margin: 15px auto 0;
  color: var(--ink-invert);
  font-size: 36px;
}

.order_actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin: 24px 0;
}

.order_button--ghost {
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: rgba(0, 0, 0, 0.31);
}

.order_phones {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 32px;
}

.order_phones a {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

@media (max-width: 1024px) {
  .offer_intro {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .offer_grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .offer_grid {
    grid-template-columns: 1fr;
  }

  .order {
    padding: 40px 20px;
  }
}
</style>
